import React from 'react';
import { Database, RefreshCw, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useInterpreterMappings, useRefreshInterpreterMappings, useUpsertInterpreterMapping } from './api';

function MappingPreview({ title, entries }: { title: string; entries: Array<[string, any]> }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="text-sm font-semibold text-gray-900 mb-3">{title}</div>
      {entries.length === 0 ? (
        <div className="text-xs text-gray-500">No entries found.</div>
      ) : (
        <div className="space-y-2">
          {entries.slice(0, 10).map(([key, value]) => (
            <div key={key} className="rounded-lg border border-gray-200 p-3">
              <div className="text-xs font-semibold text-gray-900">{key}</div>
              <pre className="mt-2 text-[10px] text-gray-600 whitespace-pre-wrap break-all">
                {JSON.stringify(value, null, 2)}
              </pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function InterpreterMappingsTab() {
  const unspscQ = useInterpreterMappings('unspsc');
  const isoQ = useInterpreterMappings('iso11783_units');
  const refreshMutation = useRefreshInterpreterMappings();
  const upsertMutation = useUpsertInterpreterMapping();

  const [type, setType] = React.useState<'unspsc' | 'iso11783_units'>('unspsc');
  const [key, setKey] = React.useState('');
  const [jsonValue, setJsonValue] = React.useState('{\n  "unspsc": "",\n  "entityType": "",\n  "category": ""\n}');
  const [patterns, setPatterns] = React.useState('[]');
  const [message, setMessage] = React.useState<string | null>(null);

  async function handleSave() {
    try {
      setMessage(null);
      const parsed = JSON.parse(jsonValue);
      const payload: Record<string, any> = { mapping: parsed };
      if (type === 'unspsc') {
        payload.patterns = JSON.parse(patterns);
      }
      await upsertMutation.mutateAsync({ type, key, payload });
      setMessage(`Saved ${type}:${key}`);
    } catch (error: any) {
      setMessage(error?.message || 'Failed to save mapping');
    }
  }

  const unspscEntries = Object.entries(unspscQ.data?.mappings || {});
  const isoEntries = Object.entries(isoQ.data?.units || {});

  return (
    <div className="space-y-6 pt-4 animate-fade-in">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Mapping Catalogs</h2>
          <p className="text-xs text-gray-500 mt-1">
            Edit the DB-backed UNSPSC and ISO catalogs, then refresh the interpreter runtime cache.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => refreshMutation.mutate()}
          disabled={refreshMutation.isPending}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          {refreshMutation.isPending ? 'Refreshing...' : 'Refresh Runtime Cache'}
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-1 bg-white rounded-xl border border-gray-200 p-4 space-y-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <Database className="w-4 h-4 text-indigo-600" />
            Save Mapping
          </div>

          <div className="space-y-1">
            <div className="text-xs text-gray-500">Mapping type</div>
            <select
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              value={type}
              onChange={(event) => setType(event.target.value as 'unspsc' | 'iso11783_units')}
            >
              <option value="unspsc">UNSPSC</option>
              <option value="iso11783_units">ISO 11783 Units</option>
            </select>
          </div>

          <div className="space-y-1">
            <div className="text-xs text-gray-500">Key</div>
            <Input value={key} onChange={(event) => setKey(event.target.value)} placeholder="soil_moisture_sensor or kg/ha" />
          </div>

          <div className="space-y-1">
            <div className="text-xs text-gray-500">Mapping JSON</div>
            <Textarea value={jsonValue} onChange={(event) => setJsonValue(event.target.value)} rows={10} />
          </div>

          {type === 'unspsc' ? (
            <div className="space-y-1">
              <div className="text-xs text-gray-500">Detection patterns JSON array</div>
              <Textarea value={patterns} onChange={(event) => setPatterns(event.target.value)} rows={4} />
            </div>
          ) : null}

          {message ? <div className="text-xs text-gray-600">{message}</div> : null}

          <Button onClick={handleSave} disabled={upsertMutation.isPending || !key.trim()} className="w-full">
            <Save className="w-4 h-4 mr-2" />
            {upsertMutation.isPending ? 'Saving...' : 'Save Mapping'}
          </Button>
        </div>

        <div className="xl:col-span-2 grid grid-cols-1 2xl:grid-cols-2 gap-6">
          <MappingPreview title={`UNSPSC (${unspscEntries.length})`} entries={unspscEntries} />
          <MappingPreview title={`ISO 11783 Units (${isoEntries.length})`} entries={isoEntries} />
        </div>
      </div>
    </div>
  );
}
